import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./CurrencyInput.css";
import { formatInputAmount, numberToWords } from "../../../Utils/utils";
import { t } from "i18next";
import { MAXIMUM_NUMBER_AMOUNT } from "../../../staticData";
import { useSelector } from "react-redux";
import { selectCurrencyCode } from "../../../slices/PaymentSessionDetailsSlice";
import { useFieldAnalytics } from "../../../Utils/useFieldAnalytics";
import { onDemandAmountSelectedEvent, onDemandAmountEnteredEvent } from "../../../Utils/uiAnalytics";

// Keep digits plus a single decimal point with at most 2 decimal places, and
// trim redundant leading zeros in the integer part.
const sanitizeAmount = (raw) => {
  let s = `${raw ?? ""}`.replace(/[^0-9.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  const hasDot = s.includes(".");
  let [intPart, decPart = ""] = s.split(".");
  intPart = intPart.replace(/^0+(?=\d)/, "");
  if (!hasDot) return intPart;
  if (intPart === "") intPart = "0";
  return `${intPart}.${decPart.slice(0, 2)}`;
};

// Shrink the amount text once it has more characters than fit at full size,
// so long amounts (e.g. 10,00,00,000.00) stay inside the input.
const FULL_SIZE_CHARS = 9;
const MIN_AMOUNT_SCALE = 0.6;
const getAmountScale = (formattedValue) =>
  Math.max(MIN_AMOUNT_SCALE, Math.min(1, FULL_SIZE_CHARS / Math.max(`${formattedValue ?? ""}`.length, 1)));

const CurrencyInput = ({ currency, value, setValue, setError, error, isMobile }) => {
  const [textWidth, setTextWidth] = useState(1);
  const [symbolWidth, setSymbolWidth] = useState(40); // fallback default
  const inputRef = useRef(null);
  const symbolRef = useRef(null);
  const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);
  const currencyCode = useSelector(selectCurrencyCode);
  const { trackFieldSelected, trackFieldEntered, trackAutofillEntered } = useFieldAnalytics();

  // Measure symbol width after render/currency change
  useEffect(() => {
    if (symbolRef.current) {
      setSymbolWidth(symbolRef.current.getBoundingClientRect().width);
    }
  }, [currencyCode]);

  const measureTextWidth = (value) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const inputStyles = window.getComputedStyle(inputRef.current);
    context.font = `${inputStyles.fontSize} ${inputStyles.fontFamily}`;
    return context.measureText(value).width / 2;
  };

  const handleInputChange = (value) => {
    const inputValue = sanitizeAmount(value);
    setError("");
    if (!inputValue || inputValue === "0" || inputValue === "0.") {
      setError(t("NO_AMOUNT_ERROR_ON_DEMAND"));
    } else {
      const numericValue = parseFloat(inputValue);
      const integerDigits = inputValue.split(".")[0].replace(/\D/g, "");
      if (!(numericValue > 0)) {
        setError(t("NO_AMOUNT_ERROR_ON_DEMAND"));
      } else if (integerDigits.length > 9) {
        setError(t("HIGH_AMOUNT_ERROR_ON_DEMAND"));
        return;
      } else if (numericValue > MAXIMUM_NUMBER_AMOUNT) {
        setError(t("HIGH_AMOUNT_ERROR_ON_DEMAND"));
      } else {
        setError("");
      }
    }

    const formattedValue = formatInputAmount(inputValue, currency);
    setValue(formattedValue);
  };

  const formattedAmount = formatInputAmount(value, currency);
  const amountScale = getAmountScale(formattedAmount);

  // Measure after render so the width uses the already-scaled font size.
  useLayoutEffect(() => {
    if (inputRef.current) {
      setTextWidth(measureTextWidth(formattedAmount));
    }
  }, [formattedAmount, amountScale]);

  useEffect(() => {
    if (totalPaymentSummaryValue) {
      handleInputChange(`${totalPaymentSummaryValue}`);
    }
  }, []);

  return (
    <div className="currency-input-wrapper">
      <div className="currency-input" style={{ "--amount-scale": amountScale }}>
        <span
          ref={symbolRef}
          className="currency-symbol-onDemand regular48Heading"
          style={{ left: `calc(50% - ${(symbolWidth < 50 ? 50 : (symbolWidth * 0.8)) + textWidth}px)` }}
        >
          {currencyCode}
        </span>
        <input
          data-testid="currency-input"
          ref={inputRef}
          type="text"
          value={formattedAmount}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => trackFieldSelected(onDemandAmountSelectedEvent)}
          onInput={(e) => trackAutofillEntered(e, onDemandAmountEnteredEvent)}
          onBlur={() => trackFieldEntered(onDemandAmountEnteredEvent, value)}
          placeholder="0"
        />
      </div>
      {error && (
        <p className="regular12Heading" data-testid="error-in-words" style={{ color: "#FF4D4F" }}>
          {error}
        </p>
      )}
      <p className="amount-in-words" data-testid="amount-in-words" style={{ marginTop: "15px" }}>
        {value && value !== 0 ? numberToWords(currency, value) : ""}
      </p>
    </div>
  );
};

export default CurrencyInput;